import AtomicConsentView from '@/components/atomic/AtomicConsentView';
import FadeInBox from '@/components/common/FadeInBox';
import { FlexRow } from '@/components/common/Flex';
import PlentyLogo from '@/components/common/PlentyLogo';
import { BigHeader, Text } from '@/components/common/Typography';
import { IdvProgressBar } from '@/components/idv/IdvProgressBar';
import { useTrackModalShown } from '@/services/analytics/useTracking';
import { useMutation } from '@apollo/client';
import { FlexProps } from '@chakra-ui/react';
import { assert } from '@common/errors';
import {
    StartJointAccountOpeningDocument,
    UpdateUserProfileDocument,
} from '@common/graphql/generated';
import { useCurrentUser } from '@common/react/user';
import styleguide from '@common/styleguide';
import { openAtomicJointAccount20240405 } from '@common/terms';
import { useEffect, useState } from 'react';
import { FlexCol } from '../common/Flex';
import { JointPlentyAccount } from '../common/icons';
import { InfoButtonWithModal } from '../common/InfoModal';
import InputField from '../common/InputField';
import Modal from '../common/modals/Modal';
import { useVGSContext, VGSProvider } from '../common/VGSProvider';
import ModalManager, { useModal } from '../ModalManager';

export interface OpenJointAccountModalProps {
    title: string;
    showProgressBar: boolean;
    onComplete?: VoidFunction;
}

export const showOpenJointAccountModal = (props: OpenJointAccountModalProps) =>
    ModalManager.show(OpenJointAccountModal, props);

const OpenJointAccountModal = ModalManager.create<OpenJointAccountModalProps>(
    (props) => (
        <VGSProvider>
            <OpenJointAccountModalInner {...props} />
        </VGSProvider>
    )
);

const OpenJointAccountModalInner = ({
    title,
    showProgressBar,
    onComplete,
}: OpenJointAccountModalProps) => {
    useTrackModalShown('Open Joint Account');

    const modal = useModal();
    const {
        userInfo,
        activePartnershipId,
        partnerInfo,
        plentyJointAccountStatus,
    } = useCurrentUser();
    const [updateUserProfile] = useMutation(UpdateUserProfileDocument);
    const [startJointAccountOpening] = useMutation(
        StartJointAccountOpeningDocument
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { tokenizeSSN } = useVGSContext();
    const [ssnErrorMessage, setSSNErrorMessage] = useState('');

    const userFirstName = userInfo.firstName;
    const userLastName = userInfo.lastName;

    const partnerUserId = partnerInfo?.userId;
    const partnerFirstName = partnerInfo?.firstName;
    const partnerLastName = partnerInfo?.lastName;

    const userProps: AccountHolderProps | null =
        userFirstName && userLastName
            ? {
                  firstName: userFirstName,
                  lastName: userLastName,
                  isSelf: true,
              }
            : null;
    const partnerProps: AccountHolderProps | null =
        partnerFirstName && partnerLastName
            ? {
                  firstName: partnerFirstName,
                  lastName: partnerLastName,
                  isSelf: false,
              }
            : null;
    const [partnerForm, setPartnerForm] = useState<PartnerInputsForm>({
        firstName: '',
        lastName: '',
    });
    const onChangePartnerForm = (inputs: Partial<PartnerInputsForm>) => {
        setPartnerForm({
            ...partnerForm,
            ...inputs,
        });
    };

    const insufficientData = !userProps || !activePartnershipId;

    useEffect(() => {
        if (insufficientData) {
            console.error('Insufficient data to open joint account modal');
            modal.remove();
        }
    }, [insufficientData, modal]);

    if (insufficientData) return <></>;

    const consentInput = {
        partnerUserId,
        partnerFirstName: partnerProps
            ? partnerProps.firstName
            : partnerForm.firstName,
        partnerLastName: partnerProps
            ? partnerProps.lastName
            : partnerForm.lastName,
        title: openAtomicJointAccount20240405.title,
        method: 'BUTTON' as const,
        contents: openAtomicJointAccount20240405.contents,
        buttonText: 'Continue',
    };

    const ssnInputRequiredForUser = !userInfo.taxIdToken;

    const onSubmit = async () => {
        setIsSubmitting(true);
        try {
            // If SSN input is used, submit that before submitting consent
            if (ssnInputRequiredForUser) {
                assert(tokenizeSSN);
                const { errorMessage, taxIdToken } = await tokenizeSSN();

                if (errorMessage) {
                    setSSNErrorMessage(errorMessage);
                    throw new Error('SSN error: ' + errorMessage);
                }

                setSSNErrorMessage('');

                await updateUserProfile({
                    variables: { patch: { taxIdToken } },
                });
            }

            await startJointAccountOpening({
                variables: consentInput,
            });

            modal.remove();
            onComplete?.();
        } finally {
            setIsSubmitting(false);
        }
    };

    const isDisabled =
        isSubmitting ||
        (!partnerProps && (!partnerForm.firstName || !partnerForm.lastName));

    return (
        <Modal
            size="4xl"
            isOpen={modal.visible}
            onClose={() => modal.remove()}
            scrollBehavior="inside"
            isCentered={false}
            isFullScreen
            closeOnOverlayClick={false}
        >
            <FadeInBox
                direction="column"
                w="full"
                h="full"
                justifyContent="center"
            >
                <FlexCol flex={1} px={10} overflow="hidden">
                    <FlexRow justify="space-between" align="center" mb={8}>
                        <PlentyLogo />
                        {showProgressBar && (
                            <IdvProgressBar forceStep="consent" />
                        )}
                    </FlexRow>
                    <FlexCol basis={0} grow={1} position="relative">
                        <AtomicConsentView
                            title={
                                <FlexRow alignItems="center" gap={2} mb={4}>
                                    <JointPlentyAccount
                                        color={styleguide.colors.blue}
                                        size={10}
                                    />
                                    <BigHeader>{title}</BigHeader>
                                </FlexRow>
                            }
                            description="This is a one time confirmation to open your joint investment account (aka a joint brokerage account)."
                            accountHolderContent={
                                <AccountHoldersSection
                                    userProps={userProps}
                                    {...(partnerProps
                                        ? { partnerProps }
                                        : { partnerForm, onChangePartnerForm })}
                                    hasPartnerConsented={
                                        plentyJointAccountStatus ===
                                        'PARTNER_CONSENTED'
                                    }
                                    ssnInputRequiredForUser={
                                        ssnInputRequiredForUser
                                    }
                                    ssnErrorMessage={ssnErrorMessage}
                                />
                            }
                            onSubmit={onSubmit}
                            isLoading={isSubmitting}
                            isDisabled={isDisabled}
                            consentInput={consentInput}
                        />
                    </FlexCol>
                </FlexCol>
            </FadeInBox>
        </Modal>
    );
};

interface AccountHolderProps {
    firstName: string;
    lastName: string;
    isSelf: boolean;
}

interface PartnerInputsForm {
    firstName: string;
    lastName: string;
}

type AccountHoldersSectionProps =
    | {
          userProps: AccountHolderProps;
          partnerProps: AccountHolderProps;
          hasPartnerConsented: boolean;
          ssnInputRequiredForUser: boolean;
          ssnErrorMessage: string | undefined;
      }
    | {
          userProps: AccountHolderProps;
          partnerForm: PartnerInputsForm;
          onChangePartnerForm: (props: Partial<PartnerInputsForm>) => void;
          hasPartnerConsented: boolean;
          ssnInputRequiredForUser: boolean;
          ssnErrorMessage: string | undefined;
      };

const AccountHoldersSection = ({
    userProps,
    hasPartnerConsented,
    ssnInputRequiredForUser,
    ssnErrorMessage,
    ...props
}: AccountHoldersSectionProps) => {
    const userOrder = hasPartnerConsented ? 2 : 1;
    const partnerOrder = hasPartnerConsented ? 1 : 2;
    return (
        <FlexRow gap={16}>
            {/* User lockup */}
            <AccountHolderLockup
                accountHolderNumber={userOrder}
                order={userOrder}
                {...userProps}
            />
            {/* User SSN (if needed) */}
            {ssnInputRequiredForUser && (
                <FlexCol order={2}>
                    <InputField
                        label={`Account holder #${userOrder} SSN`}
                        name="ssn"
                        type="ssn"
                        placeholder="Enter your SSN"
                        onChange={() => null}
                        error={ssnErrorMessage}
                        labelProps={{ color: 'textMuted2X' }}
                    />
                </FlexCol>
            )}
            {/* Partner lockup */}
            {'partnerProps' in props ? (
                <AccountHolderLockup
                    accountHolderNumber={partnerOrder}
                    order={partnerOrder}
                    {...props.partnerProps}
                />
            ) : (
                <PartnerInputs
                    form={props.partnerForm}
                    order={partnerOrder}
                    onChangeForm={props.onChangePartnerForm}
                    accountHolderNumber={partnerOrder}
                />
            )}
        </FlexRow>
    );
};

interface AccountHolderLockupProps extends FlexProps {
    firstName: string;
    lastName: string;
    accountHolderNumber: number;
    isSelf: boolean;
}

const AccountHolderLockup = ({
    firstName,
    lastName,
    accountHolderNumber,
    isSelf,
    ...props
}: AccountHolderLockupProps) => {
    return (
        <FlexCol {...props}>
            <Text fontSize="sm" color="textMuted2X" whiteSpace="nowrap" mb={2}>
                {`Account holder #${accountHolderNumber}${
                    isSelf ? ` (you)` : ``
                }`}
            </Text>
            <FlexCol grow={1} justify="center">
                <Text
                    fontWeight="medium"
                    whiteSpace="nowrap"
                    mb={0.5}
                >{`${firstName} ${lastName}`}</Text>
            </FlexCol>
        </FlexCol>
    );
};

interface PartnerInputsProps extends FlexProps {
    form: PartnerInputsForm;
    accountHolderNumber: number;
    onChangeForm: (params: Partial<PartnerInputsForm>) => void;
}

const PartnerInputs = ({
    form,
    accountHolderNumber,
    onChangeForm,
    ...props
}: PartnerInputsProps) => {
    return (
        <FlexCol {...props}>
            <FlexRow align="center" gap={1} mb={2}>
                <Text fontSize="sm" color="textMuted2X" whiteSpace="nowrap">
                    {`Account holder #${accountHolderNumber}`}
                </Text>
                <InfoButtonWithModal
                    buttonProps={{
                        h: 4,
                        onClickEventParams: {
                            location: 'Open Joint Account Modal',
                        },
                    }}
                    title="Why do we need this information?"
                >
                    <Text>
                        {
                            'We use this information to confirm your partner’s identity so we can open your joint brokerage account.'
                        }
                    </Text>
                </InfoButtonWithModal>
            </FlexRow>
            <FlexRow align="center" gap={2} whiteSpace="nowrap">
                <InputField
                    name="firstName"
                    label="Partner’s legal first name"
                    type="text"
                    value={form.firstName}
                    onChange={(firstName) => onChangeForm({ firstName })}
                    inputOptions={{ toUpperFirst: true }}
                    inputProps={{
                        variant: 'outline',
                        size: 'sm',
                    }}
                />
                <InputField
                    name="lastName"
                    label="Partner’s legal last name"
                    type="text"
                    value={form.lastName}
                    onChange={(lastName) => onChangeForm({ lastName })}
                    inputProps={{
                        variant: 'outline',
                        size: 'sm',
                    }}
                />
            </FlexRow>
        </FlexCol>
    );
};

export default OpenJointAccountModal;
